/**
 * BriefForm — massing / unit-mix / constraints controls, ported faithfully
 * from the v3 wireframe (wireframe-steps-v3 BriefForm). `only` renders a single
 * section (used as the control inside each guided ChatCard).
 */
import { Icon, ACCENT } from "./Icon";
import { UNIT_MIX, mixTotal, rebalanceMix, type BoardBrief, type MixEntry } from "./briefModel";

type Section = "massing" | "mix" | "constraints";

export function BriefForm({
  brief,
  setBrief,
  only,
}: {
  brief: BoardBrief;
  setBrief: (b: BoardBrief) => void;
  only?: Section;
}) {
  const set = (patch: Partial<BoardBrief>) => setBrief({ ...brief, ...patch });
  const total = mixTotal(brief.mix);

  const setMixVal = (i: number, v: number) => {
    const next: MixEntry[] = brief.mix.map((m, j) =>
      j === i ? { ...m, pct: Math.max(0, Math.min(100, Math.round(+v || 0))) } : { ...m }
    );
    setBrief({ ...brief, mix: rebalanceMix(next, i) });
  };
  const toggleOn = (i: number) => {
    const next: MixEntry[] = brief.mix.map((m, j) =>
      j === i ? { ...m, on: !m.on, lock: !m.on ? m.lock : false } : { ...m }
    );
    setBrief({ ...brief, mix: rebalanceMix(next, null) });
  };
  const toggleLock = (i: number) => {
    const next: MixEntry[] = brief.mix.map((m, j) =>
      j === i ? { ...m, lock: !m.lock } : { ...m }
    );
    setBrief({ ...brief, mix: next });
  };

  const massing = (
    <div>
      {!only && (
        <div className="text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af] mb-1">
          Massing
        </div>
      )}
      <Stepper
        label="Floors"
        value={brief.floors}
        on={(v) => set({ floors: v })}
        min={1}
        max={14}
      />
      <Stepper
        label="Floor height"
        value={brief.floorHeight}
        on={(v) => set({ floorHeight: v })}
        min={2.6}
        max={4.5}
        step={0.1}
        unit="m"
      />
      <Stepper
        label="Separation"
        value={brief.separation}
        on={(v) => set({ separation: v })}
        min={4}
        max={30}
        unit="m"
      />
    </div>
  );

  const mix = (
    <div>
      <div className={"flex items-center mb-1.5 " + (only ? "justify-end" : "justify-between")}>
        {!only && (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af]">
            Unit mix
          </span>
        )}
        <span
          className={
            "text-[11px] tabular-nums " +
            (total === 100 ? "text-[#9ca3af]" : "text-[#7c5cff] font-semibold")
          }
        >
          {total}%{total !== 100 ? " — must total 100" : ""}
        </span>
      </div>
      <div>
        {brief.mix.map((m, i) => (
          <MixRow
            key={i}
            m={m}
            i={i}
            onToggleOn={toggleOn}
            onSetVal={setMixVal}
            onToggleLock={toggleLock}
          />
        ))}
      </div>
      <p className="text-[10px] text-[#9ca3af] leading-snug mt-2">
        Turn a category off, type an exact %, or lock one to pin it — the unlocked categories
        rebalance to total 100%.
      </p>
    </div>
  );

  // Rough brief-time estimate (tighter spacing → more coverage). The real
  // per-zone GRZ/GFZ are computed by the engine and shown in the inspector.
  const grz = Math.max(0.2, Math.min(0.6, 0.5 - ((brief.separation || 12) - 12) * 0.012));
  const gfz = grz * (brief.floors || 6);
  const podium = brief.podium;
  const setPodium = (patch: Partial<BoardBrief["podium"]>) =>
    set({ podium: { ...podium, ...patch } });

  const constraints = (
    <div>
      {!only && (
        <div className="text-[10px] font-semibold uppercase tracking-wide text-[#9ca3af] mb-1 mt-1">
          Constraints
        </div>
      )}

      {/* building-height RANGE */}
      <div className="py-2 border-b border-dashed border-[#e5e7eb]">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[12px] text-[#4b5563]">Building height range</span>
          <span className="text-[11px] tabular-nums text-[#6b7280]">
            {brief.minHeight}–{brief.maxHeight} m
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-[#9ca3af] w-6">min</span>
            <StepBtn onClick={() => set({ minHeight: Math.max(6, brief.minHeight - 2) })}>
              –
            </StepBtn>
            <span className="w-10 text-center text-[12px] tabular-nums text-[#1f2937]">
              {brief.minHeight}m
            </span>
            <StepBtn
              onClick={() => set({ minHeight: Math.min(brief.maxHeight, brief.minHeight + 2) })}
            >
              +
            </StepBtn>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-[#9ca3af] w-6">max</span>
            <StepBtn
              onClick={() => set({ maxHeight: Math.max(brief.minHeight, brief.maxHeight - 2) })}
            >
              –
            </StepBtn>
            <span className="w-10 text-center text-[12px] tabular-nums text-[#1f2937]">
              {brief.maxHeight}m
            </span>
            <StepBtn onClick={() => set({ maxHeight: Math.min(120, brief.maxHeight + 2) })}>
              +
            </StepBtn>
          </div>
        </div>
      </div>

      {/* site coverage GRZ — rough estimate; real value is per-zone post-gen */}
      <div className="flex items-center justify-between py-2 border-b border-dashed border-[#e5e7eb]">
        <span className="text-[12px] text-[#4b5563]">
          Site coverage <span className="text-[#9ca3af]">(GRZ)</span>
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="text-[12px] tabular-nums text-[#374151]">~{grz.toFixed(2)}</span>
          <span className="text-[9px] text-[#9ca3af] border border-[#e5e7eb] bg-[#f9fafb] rounded-sm px-1 py-0.5">
            estimate · GFZ ~{gfz.toFixed(2)}
          </span>
        </span>
      </div>

      {/* commercial podium */}
      <div className="py-2">
        <label className="flex items-center justify-between gap-2 cursor-pointer">
          <span className="text-[12px] text-[#4b5563]">Commercial podium</span>
          <button
            type="button"
            onClick={() => setPodium({ on: !podium.on })}
            role="switch"
            aria-checked={podium.on}
            className={"w-9 h-5 rounded-full relative transition-colors"}
            style={{ background: podium.on ? ACCENT : "#d1d5db" }}
          >
            <span
              className={
                "absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all " +
                (podium.on ? "left-[18px]" : "left-0.5")
              }
            />
          </button>
        </label>
        {podium.on && (
          <div
            className="mt-2 pl-2 border-l-2 space-y-1.5"
            style={{ borderColor: "rgba(124,92,255,0.3)" }}
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-[#6b7280]">Podium floors</span>
              <div className="flex items-center gap-1.5">
                <StepBtn onClick={() => setPodium({ floors: Math.max(1, podium.floors - 1) })}>
                  –
                </StepBtn>
                <span className="w-8 text-center text-[12px] tabular-nums text-[#1f2937]">
                  {podium.floors}
                </span>
                <StepBtn onClick={() => setPodium({ floors: Math.min(4, podium.floors + 1) })}>
                  +
                </StepBtn>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-[#6b7280]">Podium floor height</span>
              <div className="flex items-center gap-1.5">
                <StepBtn
                  onClick={() =>
                    setPodium({ height: Math.max(3, +(podium.height - 0.5).toFixed(1)) })
                  }
                >
                  –
                </StepBtn>
                <span className="w-10 text-center text-[12px] tabular-nums text-[#1f2937]">
                  {podium.height}m
                </span>
                <StepBtn
                  onClick={() =>
                    setPodium({ height: Math.min(8, +(podium.height + 0.5).toFixed(1)) })
                  }
                >
                  +
                </StepBtn>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (only === "massing") return massing;
  if (only === "mix") return mix;
  if (only === "constraints") return constraints;
  return (
    <div className="grid grid-cols-1 gap-y-5">
      {massing}
      {mix}
      {constraints}
    </div>
  );
}

function StepBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-6 h-6 grid place-items-center rounded-sm border border-[#d1d5db] text-[#6b7280] hover:bg-[#f3f4f6]"
    >
      {children}
    </button>
  );
}

function Stepper({
  label,
  value,
  on,
  min,
  max,
  step = 1,
  unit,
}: {
  label: string;
  value: number;
  on: (v: number) => void;
  min: number;
  max: number;
  step?: number;
  unit?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-2 py-2 border-b border-dashed border-[#e5e7eb] last:border-0">
      <span className="text-[12px] text-[#4b5563]">{label}</span>
      <div className="flex items-center gap-1.5">
        <StepBtn onClick={() => on(Math.max(min, +(value - step).toFixed(2)))}>–</StepBtn>
        <span className="w-14 text-center text-[13px] tabular-nums text-[#1f2937] font-medium">
          {value}
          {unit}
        </span>
        <StepBtn onClick={() => on(Math.min(max, +(value + step).toFixed(2)))}>+</StepBtn>
      </div>
    </div>
  );
}

function MixRow({
  m,
  i,
  onToggleOn,
  onSetVal,
  onToggleLock,
}: {
  m: MixEntry;
  i: number;
  onToggleOn: (i: number) => void;
  onSetVal: (i: number, v: number) => void;
  onToggleLock: (i: number) => void;
}) {
  const cat = UNIT_MIX[i];
  return (
    <div
      className={
        "flex items-center gap-2 py-1.5 border-b border-dashed border-[#e5e7eb] last:border-0 " +
        (m.on ? "" : "opacity-50")
      }
    >
      <button
        type="button"
        onClick={() => onToggleOn(i)}
        title={m.on ? "Disable category" : "Enable category"}
        className={
          "w-4 h-4 shrink-0 rounded-[3px] border grid place-items-center " +
          (m.on ? "border-transparent" : "border-[#d1d5db] bg-white")
        }
        style={m.on ? { background: cat.color } : {}}
      >
        {m.on && <Icon d={<path d="M5 13l4 4 10-10" />} size={11} className="text-white" />}
      </button>
      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: cat.color }} />
      <span className="text-[12px] text-[#374151] grow truncate">{cat.label}</span>
      <span className="text-[9px] text-[#9ca3af] tabular-nums shrink-0">{cat.code}</span>
      <div className="flex items-center shrink-0">
        <input
          type="number"
          min={0}
          max={100}
          value={m.on ? m.pct : 0}
          disabled={!m.on || m.lock}
          onChange={(e) => onSetVal(i, +e.target.value)}
          className={
            "w-12 text-right text-[12px] tabular-nums border rounded-sm px-1 py-0.5 " +
            (m.on && !m.lock
              ? "border-[#d1d5db] text-[#1f2937] bg-white"
              : "border-[#e5e7eb] text-[#9ca3af] bg-[#f9fafb]")
          }
        />
        <span className="text-[11px] text-[#9ca3af] ml-0.5">%</span>
      </div>
      <button
        type="button"
        onClick={() => onToggleLock(i)}
        disabled={!m.on}
        title={m.lock ? "Unlock (let it rebalance)" : "Lock this %"}
        className={
          "w-6 h-6 shrink-0 grid place-items-center rounded-sm border " +
          (m.lock
            ? "border-[#7c5cff] text-[#7c5cff] bg-[#7c5cff]/10"
            : "border-[#e5e7eb] text-[#d1d5db] hover:text-[#4b5563]")
        }
      >
        <Icon
          d={
            m.lock ? (
              <g>
                <rect x="5" y="11" width="14" height="9" rx="1" />
                <path d="M8 11V8a4 4 0 0 1 8 0v3" />
              </g>
            ) : (
              <g>
                <rect x="5" y="11" width="14" height="9" rx="1" />
                <path d="M8 11V8a4 4 0 0 1 7-2" />
              </g>
            )
          }
          size={12}
        />
      </button>
    </div>
  );
}
